import React, { useState, useMemo } from "react"
import { useSelector } from "react-redux"
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined"
import DoneAllOutlinedIcon from "@mui/icons-material/DoneAllOutlined"
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined"
import { DistributedIcon } from "newcomponents/custom-icons"

const PLATFORMS = [
  { name: "Distributed", key: "total", icon: DistributedIcon },
  { name: "Opened", key: "opened", icon: VisibilityOutlinedIcon },
  { name: "Responded", key: "responded", icon: DoneAllOutlinedIcon },
]

export default function SurveyDashboard({
  viewMode,
  setViewMode,
  doctorSurveyVolume = [],
  refetch,
}) {
  const mode = useSelector(state => state.Layout.layoutMode)

  const [showDropdown, setShowDropdown] = useState(false)
  const [selectedPlatforms, setSelectedPlatforms] = useState(
    PLATFORMS.map(p => p.name),
  )

  const togglePlatform = name => {
    setSelectedPlatforms(prev =>
      prev.includes(name) ? prev.filter(p => p !== name) : [...prev, name],
    )
  }

  // ---------- KPIs (top cards: total distributed / opened / responded) ----------
  const { distributedCount, openedCount, respondedCount } = useMemo(() => {
    let totalDistributed = 0
    let totalOpened = 0
    let totalResponded = 0

    ;(doctorSurveyVolume || []).forEach(row => {
      totalDistributed += row.total || 0
      totalOpened += row.opened || 0
      totalResponded += row.responded || 0
    })

    return {
      distributedCount: totalDistributed,
      openedCount: totalOpened,
      respondedCount: totalResponded,
    }
  }, [doctorSurveyVolume])

  const kpiPlatforms = [
    { name: "Distributed", icon: DistributedIcon, count: distributedCount },
    { name: "Opened", icon: VisibilityOutlinedIcon, count: openedCount },
    { name: "Responded", icon: DoneAllOutlinedIcon, count: respondedCount },
  ]

  // ---------- CHART DATA (timeline buckets) ----------
  const { buckets, maxCount, chartRows } = useMemo(() => {
    const raw = Array.isArray(doctorSurveyVolume) ? [...doctorSurveyVolume] : []

    // sort by bucket so timeline is ordered
    raw.sort((a, b) => {
      if (!a.bucket || !b.bucket) return 0
      return String(a.bucket).localeCompare(String(b.bucket))
    })

    const labels = raw.map(r => r.bucket)

    // rows like: { bucketLabel, total, opened, responded }
    const rows = raw.map(r => ({
      bucket: r.bucket,
      total: r.total || 0,
      opened: r.opened || 0,
      responded: r.responded || 0,
    }))

    // max across all metrics for y-scale
    let m = 0
    rows.forEach(r => {
      m = Math.max(m, r.total, r.opened, r.responded)
    })
    if (m === 0) m = 1 // avoid divide by 0

    return {
      buckets: labels,
      maxCount: m,
      chartRows: rows,
    }
  }, [doctorSurveyVolume])

  const isLoading = doctorSurveyVolume == null

  const displayedPlatforms = PLATFORMS.filter(p =>
    selectedPlatforms.includes(p.name),
  )

  // simple Y-axis labels: 0..maxCount in up to 5 steps
  const yTicks = useMemo(() => {
    const steps = 5
    const arr = []
    for (let i = steps; i >= 0; i--) {
      const val = Math.round((maxCount / steps) * i)
      arr.push(val)
    }
    return arr
  }, [maxCount])

  const formatBucketLabel = bucket => {
    if (!bucket) return ""
    // bucket is "YYYY-MM-DD", "YYYY-MM", or "YYYY-Wxx"
    if (viewMode === "Day" && /^\d{4}-\d{2}-\d{2}$/.test(bucket)) {
      const d = new Date(bucket)
      if (!isNaN(d)) {
        return d.toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
        })
      }
    }
    if (viewMode === "Month" && /^\d{4}-\d{2}$/.test(bucket)) {
      const [y, m] = bucket.split("-")
      const d = new Date(Number(y), Number(m) - 1, 1)
      if (!isNaN(d)) {
        return d.toLocaleDateString(undefined, {
          month: "short",
          year: "numeric",
        })
      }
    }
    // for Week or anything else just return as-is
    return bucket
  }

  return (
    <div
      className="br"
      style={{
        margin: "0 auto",
        padding: "24px",
        backgroundColor: mode === "dark" ? "#252F40" : "white",
        fontFamily: "Inter, sans-serif",
        boxShadow: "0 10px 15px rgba(0,0,0,0.1), 0 4px 6px rgba(0,0,0,0.05)",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "32px",
        }}
      >
        <h2
          style={{
            fontSize: "1.25rem",
            fontWeight: 600,
            color: mode === "dark" ? "white" : "#1F2937",
          }}
        >
          Survey Volume
        </h2>

        <div
          style={{
            display: "flex",
            gap: "8px",
            backgroundColor: "#F3F4F6",
            borderRadius: "8px",
            padding: "6px",
            alignItems: "center",
            position: "relative",
          }}
        >
          {/* Time period buttons – wired to parent viewMode */}
          {["Day", "Week", "Month"].map(period => (
            <button
              key={period}
              onClick={() => {
                setViewMode(period)
                refetch && refetch()
              }}
              style={{
                padding: "6px 16px",
                borderRadius: "6px",
                fontSize: "0.875rem",
                fontWeight: viewMode === period ? 600 : 500,
                border: "none",
                backgroundColor:
                  viewMode === period
                    ? mode === "dark"
                      ? "#374151"
                      : "#fff"
                    : "transparent",
                color:
                  viewMode === period
                    ? mode === "dark"
                      ? "#fff"
                      : "#111827"
                    : mode === "dark"
                      ? "#9CA3AF"
                      : "#4B5563",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
            >
              {period}
            </button>
          ))}

          {/* Filter Dropdown Button */}
          <div style={{ position: "relative" }}>
            <button
              style={{
                padding: "6px 10px",
                borderRadius: 6,
                border: "none",
                background: "transparent",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: mode === "dark" ? "#9CA3AF" : "#6B6B6C",
                cursor: "pointer",
              }}
              onClick={() => setShowDropdown(prev => !prev)}
            >
              <FilterAltOutlinedIcon />
            </button>

            {/* Dropdown */}
            {showDropdown && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 8px)",
                  right: 0,
                  background: mode === "dark" ? "#1F2937" : "#fff",
                  boxShadow:
                    mode === "dark"
                      ? "0 4px 12px rgba(0,0,0,0.5)"
                      : "0 4px 12px rgba(0,0,0,0.1)",
                  borderRadius: 8,
                  padding: "8px",
                  zIndex: 20,
                  minWidth: "160px",
                  border:
                    mode === "dark" ? "1px solid #374151" : "1px solid #E5E7EB",
                }}
              >
                {PLATFORMS.map(platform => (
                  <label
                    key={platform.name}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      cursor: "pointer",
                      padding: "8px",
                      fontSize: "0.875rem",
                      color: mode === "dark" ? "#D1D5DB" : "#111827",
                      borderRadius: "4px",
                      transition: "background 0.2s",
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.backgroundColor =
                        mode === "dark" ? "#374151" : "#F3F4F6"
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.backgroundColor = "transparent"
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedPlatforms.includes(platform.name)}
                      onChange={() => togglePlatform(platform.name)}
                      style={{ cursor: "pointer" }}
                    />
                    {platform.name}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-around",
          marginBottom: "32px",
          gap: "16px",
        }}
      >
        {kpiPlatforms.map((p, i) => {
          const Icon = p.icon
          return (
            <div
              key={i}
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "12px",
                padding: "16px",
                borderRadius: "8px",
                minHeight: "80px",
                flex: 1,
                boxShadow:
                  mode === "dark"
                    ? "0px 5px 14px 0px rgba(0,0,0,0.5)"
                    : "0px 5px 14px 0px #E5E7EB",
              }}
            >
              {Icon && (
                <Icon
                  style={{
                    color: mode === "dark" ? "#60A5FA" : "#2990DF",
                    fontSize: 32,
                  }}
                />
              )}
              <div>
                <div
                  style={{
                    fontSize: "1.875rem",
                    fontWeight: "bold",
                    color: mode === "dark" ? "#fff" : "#1F2937",
                  }}
                >
                  {isLoading ? "…" : p.count}
                </div>
                <div
                  style={{
                    fontSize: "0.875rem",
                    color: mode === "dark" ? "#94A3B8" : "#6B7280",
                  }}
                >
                  {p.name}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Chart – grouped bars per time bucket */}
      <div
        style={{
          position: "relative",
          height: "16rem",
          paddingTop: "16px",
        }}
      >
        {chartRows.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: 40,
              color: mode === "dark" ? "#9CA3AF" : "#6B7280",
            }}
          >
            No survey data available for this period
          </div>
        ) : (
          <>
            {/* Y-axis */}
            <div
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: "32px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                fontSize: "0.75rem",
                color: mode === "dark" ? "#9CA3AF" : "#6B7280",
                width: "32px",
              }}
            >
              {yTicks.map((val, i) => (
                <span key={i}>{val}</span>
              ))}
            </div>

            {/* Bars Container */}
            <div
              style={{
                marginLeft: "40px",
                height: "100%",
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "space-around",
                gap: "24px",
                borderBottom: `1px solid ${
                  mode === "dark" ? "#374151" : "#D1D5DB"
                }`,
                borderLeft: `1px solid ${
                  mode === "dark" ? "#374151" : "#D1D5DB"
                }`,
                paddingBottom: "8px",
              }}
            >
              {chartRows.map((row, idx) => (
                <div
                  key={row.bucket || idx}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    flex: 1,
                    maxWidth: "120px",
                  }}
                >
                  {/* bars for this bucket */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-end",
                      justifyContent: "center",
                      gap: "6px",
                      height: "12rem",
                      width: "100%",
                    }}
                  >
                    {displayedPlatforms.map(p => {
                      const value =
                        p.key === "total"
                          ? row.total
                          : p.key === "opened"
                            ? row.opened
                            : row.responded

                      const barHeight =
                        maxCount > 0 ? (value / maxCount) * 100 : 0

                      return (
                        <div
                          key={p.name}
                          style={{
                            flex: 1,
                            maxWidth: "12px",
                            backgroundColor:
                              p.name === "Distributed"
                                ? "#60A5FA"
                                : p.name === "Opened"
                                  ? "#34D399"
                                  : "#FBBF24",
                            borderTopLeftRadius: "4px",
                            borderTopRightRadius: "4px",
                            height: `${barHeight}%`,
                            minHeight: value > 0 ? "4px" : "0",
                            transition: "all 0.2s ease",
                          }}
                          title={`${p.name}: ${value}`}
                        />
                      )
                    })}
                  </div>

                  {/* X label */}
                  <div
                    style={{
                      marginTop: "8px",
                      fontSize: "0.75rem",
                      color: mode === "dark" ? "#D1D5DB" : "#374151",
                      textAlign: "center",
                      wordBreak: "break-word",
                    }}
                  >
                    {formatBucketLabel(row.bucket)}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
